// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Alert, Box, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import DetailsPanel from '../../Common/DetailsPanel';
import { RetryAlert } from '../../Common/ErrorMessages';
import ListTable from '../../Common/ListTable';
import { ListColumn } from '../../Common/ListTable';
import { Locale, useLocales } from '../../../utils/locales';
import { convertDateTimePretty } from '../../../utils/ReadableConverter';
import { BASE_RATES_ROUTE } from '../BaseRates/baseRates';
import { ChangedModel } from '../ChangeSetImpact/changeSetImpact';
import ChangeSetImpactPanel from '../ChangeSetImpact/ChangeSetImpactPanel';
import ChangeSetReview, { ReviewOutcomeFlash } from '../ChangeSetReview/ChangeSetReview';
import { NOT_FOUND_MESSAGE, ReviewOutcome } from '../ChangeSetReview/changeSetReview';
import { formatLabel } from '../PricingModels/pricingModels';
import { LoadClient, useLoaded } from '../ScopingModels/useLoaded';
import { terGroupRoute } from '../TerGroups/terGroups';
import * as changeSets from './changeSets';
import { ProposedValues } from './ProposedValues';

const APPROVE_DESCRIPTIONS: Record<changeSets.ModelInputType, string> = {
  baseRate:
    'Approving makes these base rates live and makes each regenerated model live from them.',
  terGroup:
    'Approving makes these TERs live and makes each regenerated scoping model live from them.',
};

const modelColumns = (
  sessionOrganizationId: string | number | undefined
): ListColumn<ChangedModel>[] => [
  {
    id: 'name',
    header: 'Name',
    cell: (model): React.ReactNode => {
      const route = changeSets.modelRoute(model, sessionOrganizationId);
      const name = changeSets.modelName(model);
      return route ? <Link to={route}>{name}</Link> : name;
    },
  },
  { id: 'type', header: 'Type', cell: (model): string => changeSets.modelLabel(model.entityType) },
  {
    id: 'organization',
    header: 'Organization',
    cell: (model): string => model.organizationId || '-',
  },
];

const ModelsTable = ({
  id,
  title,
  description,
  models,
  sessionOrganizationId,
  empty,
}: {
  id: string;
  title: string;
  description: string;
  models: ChangedModel[];
  sessionOrganizationId: string | number | undefined;
  empty: string;
}): JSX.Element => (
  <ListTable
    id={id}
    items={models}
    columns={modelColumns(sessionOrganizationId)}
    filterText={changeSets.modelName}
    filteringPlaceholder="Find models"
    header={
      <Header variant="h2" counter={`(${models.length})`} description={description}>
        {title}
      </Header>
    }
    empty={empty}
  />
);

const recordLink = (changeSet: changeSets.ChangeSetDetail): React.ReactNode => {
  if (changeSet.entityType === 'baseRate') {
    return <Link to={BASE_RATES_ROUTE}>Base rates</Link>;
  }
  if (changeSet.entityType === 'terGroup') {
    return (
      <Link to={terGroupRoute(changeSet.entityId)}>
        {changeSet.entityName || changeSet.entityId}
      </Link>
    );
  }
  return (
    <Link to={changeSets.modelPageRoute(changeSet.entityType, changeSet.entityId)}>
      {changeSet.entityName || changeSet.entityId}
    </Link>
  );
};

const TITLES: Record<changeSets.ChangeSetDetail['entityType'], string> = {
  baseRate: 'Base rates change set',
  terGroup: 'TER group change set',
  pricingModel: 'Pricing model change set',
  scopingModel: 'Scoping model change set',
};

interface ChangeSetDetailProps {
  apiClient: LoadClient & { httpPost: (path: string, body: object) => Promise<unknown> };
  changeSetId: string;
  // Models of this organization link to their pages; the portal cannot show other organizations'.
  sessionOrganizationId: string | number | undefined;
  canReview: boolean;
}

const ChangeSetContent = ({
  apiClient,
  changeSet,
  sessionOrganizationId,
  canReview,
  onReviewed,
  locales,
}: {
  apiClient: ChangeSetDetailProps['apiClient'];
  changeSet: changeSets.ChangeSetDetail;
  sessionOrganizationId: string | number | undefined;
  canReview: boolean;
  onReviewed: (outcome: ReviewOutcome) => void;
  // Labels arcs by locale name; null shows their locale codes.
  locales: Locale[] | null;
}): JSX.Element => {
  const details = (
    <DetailsPanel
      items={[
        { label: 'Record', value: <span id="changeSetRecord">{recordLink(changeSet)}</span> },
        {
          label: 'Status',
          value: <span id="changeSetStatus">{formatLabel(changeSet.status)}</span>,
        },
        {
          label: 'Proposed',
          value: `${convertDateTimePretty(changeSet.createdAt)} by ${changeSet.author}`,
        },
        {
          label: 'Base version',
          value: changeSet.baseVersion === null ? '-' : changeSet.baseVersion,
        },
      ]}
    />
  );
  const header = (
    <Header variant="h1" description={`Change set ${changeSet.changeSetId}`}>
      {TITLES[changeSet.entityType]}
    </Header>
  );

  if (changeSet.entityType !== 'baseRate' && changeSet.entityType !== 'terGroup') {
    return (
      <SpaceBetween size="m">
        {header}
        {details}
        <Alert id="reviewOnModelPage" type="info">
          A model&apos;s change set is reviewed on the model&apos;s page, linked above.
        </Alert>
      </SpaceBetween>
    );
  }

  const failed = changeSet.status === 'failed';
  const review =
    canReview && changeSets.isApprovable(changeSet) ? (
      <ChangeSetReview
        key={changeSet.status}
        apiClient={apiClient}
        changeSetId={changeSet.changeSetId}
        failed={failed}
        approveDescription={APPROVE_DESCRIPTIONS[changeSet.entityType]}
        onReviewed={onReviewed}
      />
    ) : (
      undefined
    );

  return (
    <SpaceBetween size="m">
      {header}
      {details}
      <ProposedValues apiClient={apiClient} changeSet={changeSet} locales={locales} />
      <ModelsTable
        id="regeneratedModels"
        title="Regenerated models"
        description="Live models this change set regenerates from the proposed values"
        models={changeSet.regeneratedModels || []}
        sessionOrganizationId={sessionOrganizationId}
        empty="No models are regenerated."
      />
      <ModelsTable
        id="skippedModels"
        title="Skipped models"
        description="Live models it cannot regenerate, because their live version has no stored inputs"
        models={changeSet.skippedModels || []}
        sessionOrganizationId={sessionOrganizationId}
        empty="No models are skipped."
      />
      <ChangeSetImpactPanel
        apiClient={apiClient}
        changeSetId={changeSet.changeSetId}
        locales={locales}
        showOrganization={changeSet.entityType === 'terGroup'}
        actions={review}
      />
    </SpaceBetween>
  );
};

/**
 * A base-rate or TER-group change set: what it proposes, the live models it regenerates or
 * cannot, and its cost impact, with its review while it is pending or after an approval that
 * failed part way. A model change set is reviewed on its model's page, which this links to.
 */
export const ChangeSetDetail = ({
  apiClient,
  changeSetId,
  sessionOrganizationId,
  canReview,
}: ChangeSetDetailProps): JSX.Element => {
  const loaded = useLoaded<changeSets.ChangeSetDetail>(
    apiClient,
    changeSets.changeSetPath(changeSetId),
    changeSets.describeError
  );
  // A failure only leaves arcs labelled by their locale codes, so it is not shown.
  const { locales } = useLocales(apiClient, changeSets.describeError);
  const [reviewOutcome, setReviewOutcome] = useState<ReviewOutcome | null>(null);
  // The counts in Pricing's refusal of the last approval, if it failed part way.
  const [failedAttempt, setFailedAttempt] = useState<changeSets.ApplyProgress | null>(null);
  const changeSet = loaded.value;

  const reviewed = (outcome: ReviewOutcome): void => {
    const attempt = outcome.type === 'error' ? changeSets.applyProgressIn(outcome.message) : null;
    setFailedAttempt(attempt);
    setReviewOutcome(attempt ? null : outcome);
    loaded.reload();
  };

  // Shown while the reload after a failed approval is in flight too. The stored counts span all
  // the set's approvals, so they are preferred to the refusal's, which count only the last one's.
  const showFailedApproval = changeSet ? changeSet.status === 'failed' : failedAttempt !== null;
  const failedProgress =
    (changeSet && changeSets.applyProgressOf(changeSet.appliedModels, changeSet.totalModels)) ||
    failedAttempt;

  let content: JSX.Element;
  if (loaded.missing || loaded.errors.length > 0) {
    content = (
      <RetryAlert
        id="changeSetLoadError"
        header="Could not load the change set"
        messages={loaded.missing ? [NOT_FOUND_MESSAGE] : loaded.errors}
        retryId="retryChangeSet"
        onRetry={loaded.reload}
      />
    );
  } else if (!changeSet) {
    content = (
      <Box id="changeSetLoading" textAlign="center">
        <Spinner /> Loading change set
      </Box>
    );
  } else {
    content = (
      <ChangeSetContent
        apiClient={apiClient}
        changeSet={changeSet}
        sessionOrganizationId={sessionOrganizationId}
        canReview={canReview}
        onReviewed={reviewed}
        locales={locales}
      />
    );
  }

  return (
    <SpaceBetween size="m">
      <ReviewOutcomeFlash outcome={reviewOutcome} onDismiss={(): void => setReviewOutcome(null)} />
      {showFailedApproval && (
        <Alert id="changeSetFailed" type="error">
          {changeSets.failedApprovalMessage(failedProgress)}
        </Alert>
      )}
      {content}
    </SpaceBetween>
  );
};

ChangeSetDetail.propTypes = {
  apiClient: PropTypes.object.isRequired,
  changeSetId: PropTypes.string.isRequired,
  sessionOrganizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canReview: PropTypes.bool.isRequired,
};

const ChangeSetDetailView = (props: Omit<ChangeSetDetailProps, 'apiClient'>): JSX.Element => (
  <ChangeSetDetail apiClient={ApiClient} {...props} />
);

export default ChangeSetDetailView;
